// Portfolio picker — searchable combobox over the portfolio registry
// (recent first, name + loan count + total balance per row) with an inline
// "Create portfolio '{name}'" affordance (BrokeragePicker precedent). Open
// portfolios only — archived ones are hidden from assignment. Creation is
// gated Loans.Manage by the caller (the picker is only mounted on
// Manage-gated surfaces).

import { useEffect, useRef, useState } from "react";
import { Plus, Search } from "lucide-react";
import { Input } from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { usePortfolios, useCreatePortfolio } from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import type { PortfolioListItemDto } from "@/api/portfolioAdmin/types";

export interface PortfolioPickerResult {
  portfolioId: string;
  portfolioName: string;
}

interface PortfolioPickerProps {
  onSelect: (result: PortfolioPickerResult) => void;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
}

export function PortfolioPicker({
  onSelect,
  placeholder,
  autoFocus,
  disabled,
}: PortfolioPickerProps) {
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const portfolios = usePortfolios();
  const create = useCreatePortfolio();

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const query = text.trim().toLowerCase();
  const openPortfolios = (portfolios.data ?? []).filter((p) => p.status === "Open");
  const matches = query
    ? openPortfolios.filter((p) => p.name.toLowerCase().includes(query))
    : openPortfolios;
  const exactMatch = openPortfolios.some((p) => p.name.toLowerCase() === query);
  const offerCreate = query.length > 0 && !exactMatch;

  function commit(p: PortfolioListItemDto) {
    setText(p.name);
    setOpen(false);
    onSelect({ portfolioId: p.id, portfolioName: p.name });
  }

  async function createAndSelect() {
    const name = text.trim();
    if (!name) return;
    setCreateError(null);
    try {
      const created = await create.mutateAsync({ name });
      setText(created.name);
      setOpen(false);
      onSelect({ portfolioId: created.id, portfolioName: created.name });
    } catch (e) {
      setCreateError((e as Error).message ?? "Create failed.");
    }
  }

  return (
    <div ref={containerRef} className="relative" data-testid="portfolio-picker">
      <div className="relative">
        <Search
          size={14}
          strokeWidth={1.5}
          aria-hidden
          className="absolute left-space-2 top-1/2 -translate-y-1/2 text-gray-mid pointer-events-none"
        />
        <Input
          type="text"
          value={text}
          autoFocus={autoFocus}
          disabled={disabled}
          placeholder={placeholder ?? "Search portfolios…"}
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-label="Portfolio"
          className="pl-7"
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setText(e.target.value);
            setCreateError(null);
            setOpen(true);
          }}
          data-testid="portfolio-picker-input"
        />
      </div>

      {open && (
        <ul
          role="listbox"
          className="absolute z-30 mt-space-1 w-full max-h-64 overflow-auto bg-white-pure border border-border-light rounded-md shadow-lg py-space-1"
        >
          {portfolios.isLoading && (
            <li className="px-space-3 py-space-2 text-caption text-gray-mid">Loading…</li>
          )}
          {portfolios.isError && (
            <li className="px-space-3 py-space-2 text-caption text-status-error">
              Couldn't load portfolios.
            </li>
          )}
          {!portfolios.isLoading && !portfolios.isError && matches.length === 0 && (
            <li className="px-space-3 py-space-2 text-caption text-gray-mid">
              No matching portfolios.
            </li>
          )}
          {matches.map((p) => (
            <li key={p.id} role="option" aria-selected={false}>
              <button
                type="button"
                className="w-full text-left px-space-3 py-space-2 text-small hover:bg-white-warm"
                onClick={() => commit(p)}
                data-testid={`portfolio-picker-option-${p.id}`}
              >
                <span className="font-semibold">{p.name}</span>
                <span className="text-caption text-gray-mid tabular-nums">
                  {" "}
                  · {p.loanCount} loan{p.loanCount === 1 ? "" : "s"} ·{" "}
                  {formatCurrency(p.totalCurrentBalance)}
                </span>
              </button>
            </li>
          ))}
          {offerCreate && (
            <li className="border-t border-border-light mt-space-1 pt-space-1">
              <button
                type="button"
                className="w-full text-left px-space-3 py-space-2 text-small text-gold-shadow hover:bg-white-warm flex items-center gap-space-1 disabled:opacity-50"
                onClick={() => void createAndSelect()}
                disabled={create.isPending}
                data-testid="portfolio-picker-create"
              >
                <Plus size={14} strokeWidth={1.5} aria-hidden />
                Create portfolio "{text.trim()}"
              </button>
              {createError && (
                <p className="px-space-3 text-caption text-status-error m-0" role="alert">
                  {createError}
                </p>
              )}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
